import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Label } from '@givebutter/primitives/label';
import { Separator } from '@givebutter/primitives/separator';
import { fetchCampaigns } from 'api/campaigns';
import { fetchNotifications } from 'api/notifications';
import {
    NotificationToggleForm,
    isSubscribedToCampaign,
} from 'assets/js/pages/account-settings/account-settings/NotificationToggleForm';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { accountQueryKeys } from 'queries/account-query-keys';
import { notificationQueryKeys } from 'queries/notification-query-keys';
import { twJoin } from 'tailwind-merge';
import { Campaign, Notification } from 'types/domain';

const columnHelper = createColumnHelper<Campaign>();

const NotificationSettings = () => {
    const { accountId } = useCurrentAccount();
    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search);
    const { pagination, setPagination } = Table.useReactTableState();

    const { data: notifications } = useSuspenseQuery({
        queryKey: notificationQueryKeys.all(accountId),
        queryFn: () => fetchNotifications(accountId),
    });

    const campaignNotifications = useMemo(
        () => notifications.filter(n => n.account.subscribed),
        [notifications],
    );
    const hasCampaignNotifications = campaignNotifications.length > 0;

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            query: searchTerm,
        },
        500,
    );

    const { data: campaigns } = useSuspenseQuery({
        queryKey: accountQueryKeys.campaignsWithFilters(accountId, params),
        queryFn: () => fetchCampaigns(accountId, params),
        refetchOnWindowFocus: false,
    });

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: Table.Constants.Expand,
                cell: info =>
                    hasCampaignNotifications ? <Table.RowExpand row={info.row} /> : null,
            }),
            columnHelper.accessor('title', {
                header: 'Campaign',
                cell: info => <div className="max-w-sm truncate">{info.getValue()}</div>,
            }),
            columnHelper.display({
                header: 'Notifications',
                cell: info => {
                    const status = getStatus(campaignNotifications, info.row.original.id);
                    return (
                        <div className="flex items-center gap-3">
                            <div
                                className={twJoin(
                                    'h-2 w-2 rounded-full',
                                    status === 'mixed' && 'bg-orange-400',
                                    status === 'enabled' && 'bg-green-400',
                                    status === 'disabled' && 'bg-gray-400',
                                )}
                            />
                            <p className="text-sm">
                                {status === 'disabled' ? 'Disabled' : 'Enabled'}
                            </p>
                        </div>
                    );
                },
            }),
        ],
        [campaignNotifications, hasCampaignNotifications],
    );

    const table = useReactTable({
        data: campaigns?.data || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        pageCount: campaigns?.meta?.last_page ?? -1,
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        enableSorting: false,
        manualPagination: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            onRowClick: row => (hasCampaignNotifications ? () => row.toggleExpanded() : undefined),
            renderExpandedRow: row => {
                return (
                    <tr>
                        <td colSpan={row.getVisibleCells().length}>
                            <div className="flex flex-col bg-gray-50">
                                {campaignNotifications.map(item => (
                                    <NotificationToggleForm
                                        key={item.type}
                                        notification={item}
                                        modelId={row.original.id}
                                        modelType="campaign"
                                    />
                                ))}
                            </div>
                        </td>
                    </tr>
                );
            },
        },
    });

    useSyncPaginatedData(table, campaigns);

    return (
        <div className="flex flex-col gap-4">
            <Box>
                <div className="p-8">
                    <p className="text-2xl font-medium">All Campaigns</p>
                    <p className="text-base text-gray-500">
                        Disable these notifications to stop notifications for all campaigns. To
                        disable individual campaign notifications, keep these enabled and make
                        adjustments to "Individual Campaigns" below.
                    </p>
                </div>
                {notifications.map(notification => (
                    <NotificationToggleForm key={notification.type} notification={notification} />
                ))}
            </Box>
            <Table.Root isLoading={isPending} variant="bordered" table={table}>
                <div className="flex flex-col gap-4">
                    <Box className="flex flex-col overflow-hidden">
                        <div className={'flex flex-col gap-8 p-8'}>
                            <div>
                                <p className="text-2xl font-medium">Individual Campaigns</p>
                                <p className="text-base text-gray-500">
                                    These settings only apply to individual campaign email
                                    notifications.
                                </p>
                            </div>
                            <div className="self-start">
                                <Label className="sr-only" htmlFor="campaign_search">
                                    Search
                                </Label>
                                <Input.Root size={'sm'}>
                                    <Input.SearchIcon />
                                    <Input.Form
                                        id="campaign_search"
                                        placeholder="Search Campaigns"
                                        value={search}
                                        onChange={e => setSearch(e.currentTarget.value)}
                                    />
                                </Input.Root>
                            </div>
                        </div>
                        <Separator />
                        <div className="relative overflow-x-auto">
                            <Table.Content>
                                <Table.Header />
                                <Table.Body />
                            </Table.Content>
                            <Table.Empty variant={'secondary'}>
                                <Table.EmptyIcon />
                                <Table.EmptyTitle>No campaigns found</Table.EmptyTitle>
                            </Table.Empty>
                        </div>
                    </Box>
                </div>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </Table.Root>
        </div>
    );
};

type CampaignRowStatus = 'enabled' | 'mixed' | 'disabled';
const getStatus = (notifications: Notification[], campaignId: number): CampaignRowStatus => {
    if (notifications.length <= 0) return 'disabled';
    if (notifications.every(n => isSubscribedToCampaign(n, campaignId))) return 'enabled';
    if (notifications.some(n => isSubscribedToCampaign(n, campaignId))) return 'mixed';
    return 'disabled';
};

export { NotificationSettings };
